// Lightweight SEO helper — manages <head> tags without extra dependencies

const SITE_NAME = "BoutiqueOS";

const upsertMeta = (attr, key, content) => {
  if (content == null || content === "") {
    document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
    return;
  }
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href, extra = {}) => {
  const selector = `link[rel="${rel}"]` + (extra.hreflang ? `[hreflang="${extra.hreflang}"]` : "");
  let el = document.head.querySelector(selector);
  if (!href) { el?.remove(); return; }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const setJsonLd = (id, data) => {
  const scriptId = `jsonld-${id}`;
  let el = document.getElementById(scriptId);
  if (!data) { el?.remove(); return; }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = scriptId;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const applySeo = ({
  title, description, keywords = [], canonical, image, type = "website", noindex = false, jsonLd = null, jsonLdId = "page",
}) => {
  const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title}`) : SITE_NAME;
  document.title = fullTitle;
  document.documentElement.lang = "fr";
  const url = canonical || window.location.href.split("#")[0];

  upsertMeta("name", "description", description);
  upsertMeta("name", "keywords", keywords.length ? keywords.join(", ") : null);
  upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large");
  upsertLink("canonical", noindex ? null : url);
  upsertLink("alternate", noindex ? null : url, { hreflang: "fr" });

  upsertMeta("property", "og:type", type);
  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:locale", "fr_FR");
  upsertMeta("property", "og:title", fullTitle);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:image", image);
  upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
  upsertMeta("name", "twitter:title", fullTitle);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", image);

  setJsonLd(jsonLdId, jsonLd);
};

// ─── Tracking (GA4 / Google Ads / Meta Pixel) — injected once per id ───
const loaded = new Set();

export const injectTracking = ({ ga4_id, google_ads_id, meta_pixel_id } = {}) => {
  const gtagIds = [ga4_id, google_ads_id].filter(Boolean);
  gtagIds.forEach((id) => {
    if (loaded.has(id)) return;
    loaded.add(id);
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", id);
  });
  if (meta_pixel_id && !loaded.has(`px-${meta_pixel_id}`)) {
    loaded.add(`px-${meta_pixel_id}`);
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", meta_pixel_id);
    window.fbq("track", "PageView");
  }
};

export const trackEvent = (name, params = {}) => {
  try {
    window.gtag?.("event", name, params);
    window.fbq?.("trackCustom", name, params);
  } catch {}
};
